import type {
  Phien, TrangThaiExtension, DanhBa, Nhom, ChienDich, Job, TrangThaiAI, LichDinhKy,
  BaiDeBinhLuan, BoCauBinhLuan, KetQuaGuiBinhLuan,
  DanhSachMcpSession, TaoMcpSessionInput, TaoMcpSessionResponse,
  DanhSachMcpAudit, DanhSachMcpWriteProposal, McpWriteProposalActionResponse,
  BoSuuTapNhom, KetQuaThamGiaNhom, JoinBatchDTO, JoinBatchPolicyDTO,
  GroupJoinPolicyResponse, AdminJoinPolicyResponse, AdminJoinPolicyUpdateRequest,
  AdminJoinPolicyAuditsResponse,
  Goi, DonHang, TongQuanTaiKhoan, ActionCatalogResponse,
  BackgroundPresetsResponse, ProductCapabilitiesResponse,
} from './kieu';

/**
 * Lớp gọi API.
 *
 * Nguyên tắc: KHÔNG nuốt lỗi. Bản cũ để component tự gọi fetch rồi mỗi nơi xử
 * lý lỗi một kiểu, nên có chỗ hỏng mà giao diện vẫn im lặng. Ở đây mọi lỗi đều
 * ném ra dưới dạng LoiMayChu có mã rõ ràng, nơi gọi tự quyết hiển thị thế nào.
 */

export class LoiMayChu extends Error {
  constructor(
    public trangThai: number,
    public ma: string | null,
    message: string,
    public dulieu?: unknown,
  ) {
    super(message);
    this.name = 'LoiMayChu';
  }
}

export interface CustomerEvidence {id:string;groupId:string;postId:string;authorName:string|null;text:string;url:string;matchedTerms:string[];contacts:{normalized:string;excerpt:string}[];creationTime:number|null}
export interface CustomerRun {id:string;status:string;createdAt:string;coverage:{groupId:string;postsRead:number;complete:boolean;reason:string|null}[];candidates:CustomerEvidence[]}
export interface CustomerLead {id:string;status:string;note:string;evidence:CustomerEvidence}

/**
 * Hạn giờ cho MỌI lời gọi, kể cả khi máy chủ không đặt hạn giờ của nó.
 *
 * Vì sao cần: đo được ngày 14/09/2026, `/api/fbi/groups` và `/api/fbi/campaigns`
 * treo hơn 20 giây vì tầng dưới đi tải trang Facebook thật. Giao diện kẹt ở
 * khung xương đang tải, người dùng không biết chuyện gì đang xảy ra.
 *
 * Máy chủ đã được sửa, nhưng giao diện vẫn phải có hạn giờ riêng: không bao giờ
 * tin tầng dưới sẽ trả lời đúng hẹn. Thà báo "máy chủ phản hồi chậm" còn hơn
 * treo im lặng.
 */
const HAN_GIO_MAC_DINH_MS = 15000;

async function goi<T>(duongDan: string, tuyChon: RequestInit & { hanGioMs?: number } = {}): Promise<T> {
  const { hanGioMs = HAN_GIO_MAC_DINH_MS, signal: userSignal, ...conLai } = tuyChon;
  const ngat = new AbortController();
  const hen = setTimeout(() => ngat.abort(), hanGioMs);
  if (userSignal) {
    if (userSignal.aborted) {
      ngat.abort();
    } else {
      userSignal.addEventListener('abort', () => ngat.abort(), { once: true });
    }
  }

  let res: Response;
  try {
    res = await fetch(duongDan, {
      credentials: 'include',
      ...conLai,
      headers: { 'content-type': 'application/json', ...(conLai.headers || {}) },
      signal: ngat.signal,
    });
  } catch (e) {
    if (userSignal?.aborted) {
      throw e;
    }
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new LoiMayChu(0, 'QUA_HAN',
        `Máy chủ không trả lời sau ${Math.round(hanGioMs / 1000)} giây. Thử lại sau ít phút.`);
    }
    // Mất mạng, DNS hỏng, máy chủ không với tới được.
    throw new LoiMayChu(0, 'MAT_KET_NOI',
      'Không kết nối được máy chủ. Kiểm tra đường truyền rồi thử lại.');
  } finally {
    clearTimeout(hen);
  }

  const kieuND = res.headers.get('content-type') || '';
  let than: unknown = null;
  if (kieuND.includes('application/json')) {
    try { than = await res.json(); } catch { /* thân rỗng hoặc hỏng */ }
  }

  if (!res.ok) {
    const b = (than || {}) as { code?: string; message?: string; data?: unknown };
    throw new LoiMayChu(
      res.status,
      b.code || null,
      b.message || moTaTheoMa(res.status),
      b.data,
    );
  }

  // Máy chủ có thể trả HTTP 200 kèm { ok: false }. Đây là bẫy đã gặp thật:
  // /api/fbi/account/status trả 200 nhưng ok:false + EXTENSION_OFFLINE.
  const b = than as { ok?: boolean; code?: string; message?: string } | null;
  if (b && b.ok === false) {
    throw new LoiMayChu(res.status, b.code || null, b.message || 'Máy chủ từ chối yêu cầu.', than);
  }

  return than as T;
}

function moTaTheoMa(ma: number): string {
  if (ma === 401) return 'Phiên đăng nhập đã hết hạn. Hãy đăng nhập lại.';
  if (ma === 402) return 'Tính năng này cần gói trả phí.';
  if (ma === 403) return 'Bạn không có quyền thực hiện việc này.';
  if (ma === 409) return 'Trạng thái hiện tại không cho phép thao tác này.';
  if (ma === 429) return 'Bạn thao tác hơi nhanh. Chờ một chút rồi thử lại.';
  if (ma >= 500) return `Máy chủ đang bảo trì hoặc phản hồi chậm (Lỗi ${ma}). Vui lòng thử lại sau ít phút.`;
  return `Yêu cầu không thành công (Lỗi ${ma}).`;
}

// ===== Các lời gọi =====

export const api = {
  phien: () => goi<Phien>('/api/session'),
  dangXuat: () => goi<{ ok: boolean }>('/api/logout', { method: 'POST' }),

  trangThaiExtension: () => goi<TrangThaiExtension>('/api/extension/status'),
  danhBa: () => goi<DanhBa>('/api/fbi/account/directory'),

  /**
   * Bảng Tài khoản: Avatar · Tên · UID · Bạn · Nhóm.
   *
   * KHÁC `danhBa()`: `/directory` là sổ danh bạ thuần (tên + ảnh), KHÔNG có số
   * bạn bè và số nhóm. Bảng từng gọi nhầm `/directory` nên hai cột số luôn hiện
   * `—`, trong khi `/overview` vẫn trả đúng 44 nhóm cho tài khoản chính.
   *
   * Mặc định CHỈ đọc nhớ đệm. `lamMoi=true` mới cào Facebook, và chỉ cào cho
   * tư cách đang hoạt động — nên đừng gọi kèm mỗi lần mở màn hình.
   */
  tongQuanTaiKhoan: (lamMoi = false) =>
    goi<TongQuanTaiKhoan>('/api/fbi/account/overview' + (lamMoi ? '?refresh=true' : ''),
      { hanGioMs: lamMoi ? 60000 : 15000 }),

  // Hai lời gọi này từng treo 20+ giây (BUG-6). Máy chủ đã sửa, nhưng vẫn để
  // hạn giờ rộng hơn mặc định vì chúng thật sự có thể phải hỏi xuống Extension.
  nhom: (fbUid?: string) => goi<{ ok: boolean; groups: Nhom[]; reason?: string; message?: string }>(
    '/api/fbi/groups' + (fbUid ? `?fbUid=${encodeURIComponent(fbUid)}` : ''), { hanGioMs: 20000 }),
  // Đồng bộ lại danh sách nhóm là việc nặng: phải cào nhiều trang Facebook.
  dongBoNhom: () => goi<{ ok: boolean }>('/api/fbi/groups?forceSync=true', { hanGioMs: 90000 }),
  timNhom: (query: string, limit = 20) => goi<{ ok: boolean; groups: Nhom[]; total: number; message?: string }>(
    '/api/fbi/groups/search', { method: 'POST', body: JSON.stringify({ query, limit }), hanGioMs: 20000 }),
  thamGiaNhom: (groupIds: string[], idempotencyKey: string, options?: { fbUid?: string; policy?: JoinBatchPolicyDTO }) =>
    goi<KetQuaThamGiaNhom>('/api/fbi/groups/join-list', {
      method: 'POST',
      body: JSON.stringify({
        groupIds,
        fbUid: options?.fbUid,
        policy: options?.policy ? {
          delayMinSec: options.policy.delayMinSec,
          delayMaxSec: options.policy.delayMaxSec,
          roundRestSec: options.policy.roundRestSec,
        } : undefined,
      }),
      headers: { 'Idempotency-Key': idempotencyKey },
      hanGioMs: 30000,
    }),
  getGroupJoinPolicy: (fbUid?: string, signal?: AbortSignal) =>
    goi<GroupJoinPolicyResponse>(
      '/api/fbi/groups/join-policy' + (fbUid ? `?fbUid=${encodeURIComponent(fbUid)}` : ''),
      { signal }
    ),
  layChinhSachThamGiaNhom: (fbUid?: string, signal?: AbortSignal) =>
    api.getGroupJoinPolicy(fbUid, signal),
  getAdminGroupJoinPolicy: () =>
    goi<AdminJoinPolicyResponse>('/api/admin/group-join-policy'),
  updateAdminGroupJoinPolicy: (than: AdminJoinPolicyUpdateRequest, operationKey?: string) =>
    goi<{ ok: boolean; version: number; policy: any; auditId: string; idempotentReplay?: boolean; message?: string }>(
      '/api/admin/group-join-policy',
      {
        method: 'PUT',
        body: JSON.stringify(than),
        headers: operationKey ? { 'Idempotency-Key': operationKey } : undefined,
      }
    ),
  getAdminGroupJoinPolicyAudits: (limit = 50) =>
    goi<AdminJoinPolicyAuditsResponse>(
      `/api/admin/group-join-policy/audits?limit=${encodeURIComponent(limit)}`
    ),
  getJoinBatches: (fbUid?: string) =>
    goi<{ ok: boolean; batches: JoinBatchDTO[] }>(
      '/api/fbi/groups/join-batches' + (fbUid ? `?fbUid=${encodeURIComponent(fbUid)}` : ''),
      { hanGioMs: 20000 }
    ),
  customerSearchSources: (fbUid:string)=>goi<{ok:boolean;groups:{id:string;name:string;collection_name:string}[]}>('/api/fbi/customer-search/sources?fbUid='+encodeURIComponent(fbUid)),
  customerSearchLeads: (fbUid:string)=>goi<{ok:boolean;leads:CustomerLead[]}>('/api/fbi/customer-search/leads?fbUid='+encodeURIComponent(fbUid)),
  customerSearchRun: (body:unknown,key:string)=>goi<{ok:boolean;run:CustomerRun}>('/api/fbi/customer-search/runs',{method:'POST',body:JSON.stringify(body),headers:{'Idempotency-Key':key},hanGioMs:250000}),
  customerSearchGetRun: (fbUid:string,id:string)=>goi<{ok:boolean;run:CustomerRun}>('/api/fbi/customer-search/runs/'+encodeURIComponent(id)+'?fbUid='+encodeURIComponent(fbUid)),
  customerSearchOperation: (fbUid:string,key:string)=>goi<{ok:boolean;run:CustomerRun}>('/api/fbi/customer-search/operations/'+encodeURIComponent(key)+'?fbUid='+encodeURIComponent(fbUid)),
  customerSearchSaveLead: (body:unknown)=>goi<{ok:boolean;id:string}>('/api/fbi/customer-search/leads',{method:'POST',body:JSON.stringify(body)}),
  customerSearchUpdateLead: (id:string,body:unknown)=>goi<{ok:boolean}>('/api/fbi/customer-search/leads/'+encodeURIComponent(id),{method:'PATCH',body:JSON.stringify(body)}),
  customerSearchDeleteLead: (fbUid:string,id:string)=>goi<{ok:boolean}>('/api/fbi/customer-search/leads/'+encodeURIComponent(id)+'?fbUid='+encodeURIComponent(fbUid),{method:'DELETE'}),
  getJoinPlans: (fbUid?: string) => goi<{ ok: boolean; plans: import('../components/JoinPlanPanel').JoinPlan[] }>(
    '/api/fbi/groups/join-plans?fbUid=' + encodeURIComponent(fbUid || '')),
  nextJoinPlanTarget: (planId: string, expectedGroupId: string) => goi<{ ok: boolean; completed: boolean; batch?: { batchId?: string; id?: string } }>(
    '/api/fbi/groups/join-plans/' + encodeURIComponent(planId) + '/next',
    { method: 'POST', body: JSON.stringify({expectedGroupId}), headers: {'Idempotency-Key': 'plan-next-'+planId+'-'+expectedGroupId}, hanGioMs: 190000 }),
  controlJoinBatch: (batchId: string, action: 'pause' | 'resume' | 'cancel') =>
    goi<{ ok: boolean; batch: JoinBatchDTO }>(
      `/api/fbi/groups/join-batches/${encodeURIComponent(batchId)}/${encodeURIComponent(action)}`,
      { method: 'POST', body: JSON.stringify({}), hanGioMs: 20000 }
    ),
  layDanhSachBatchThamGia: (fbUid?: string) => api.getJoinBatches(fbUid),
  dieuKhienBatchThamGia: (batchId: string, action: 'pause' | 'resume' | 'cancel') =>
    api.controlJoinBatch(batchId, action),
  boSuuTapNhom: (fbUid?: string) => goi<{ ok: boolean; collections: BoSuuTapNhom[] }>(
    '/api/fbi/group-collections' + (fbUid ? `?uid=${encodeURIComponent(fbUid)}` : '')),
  taoBoSuuTapNhom: (name: string) => goi<{ ok: boolean; collection: BoSuuTapNhom }>('/api/fbi/group-collections', {
    method: 'POST',
    body: JSON.stringify({ action: 'CREATE_COLLECTION', name }),
  }),
  ganNhomVaoBoSuuTap: (fbUid: string, groupIds: string[], collectionName: string) =>
    goi<{ ok: boolean; updatedCount: number; collectionName: string }>('/api/fbi/group-collections', {
      method: 'POST',
      body: JSON.stringify({ action: 'ASSIGN_GROUPS', uid: fbUid, groupIds, collectionName }),
    }),

  chienDich: () => goi<{ ok: boolean; campaigns: ChienDich[]; total: number }>(
    '/api/fbi/campaigns', { hanGioMs: 20000 }),
  congViec: () => goi<{ ok: boolean; jobs: Job[]; total: number }>(
    '/api/fbi/jobs', { hanGioMs: 20000 }),
  congViecTheoChienDich: (id: string) => goi<{ ok: boolean; jobs: Job[]; total: number }>(
    `/api/fbi/campaigns/${encodeURIComponent(id)}/jobs`),

  /**
   * Tạo chiến dịch đăng bài. ĐÂY LÀ THAO TÁC GHI LÊN FACEBOOK THẬT.
   *
   * Máy chủ kiểm tra lại toàn bộ trong `lab-server/schemas/dispatch.schema.js`.
   * Giao diện cũng kiểm trước, nhưng chỉ để báo lỗi sớm cho khách — KHÔNG được
   * coi đó là đủ. Máy chủ mới là nơi quyết định, vì API có thể bị gọi thẳng.
   */
  taoChienDich: (than: Record<string, unknown>) =>
    goi<{ ok: boolean; code?: string; message?: string; data?: { campaignId: string; jobIds: string[]; total: number } }>(
      '/api/fbi/dispatch', { method: 'POST', body: JSON.stringify(than), hanGioMs: 30000 }),
  xoaChienDich: (id: string) =>
    goi<{ ok: boolean }>(`/api/fbi/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  dieuKhienChienDich: (id: string, hanhDong: 'pause' | 'resume' | 'abort') =>
    goi<{ ok: boolean; updated: number }>(
      `/api/fbi/campaigns/${encodeURIComponent(id)}/${hanhDong}`, { method: 'POST' }),
  dieuKhienCongViec: (id: string, hanhDong: 'retry' | 'cancel') =>
    goi<{ ok: boolean; status?: string }>(
      `/api/fbi/jobs/${encodeURIComponent(id)}/${hanhDong}`, { method: 'POST' }),
  chayLaiChienDich: (id: string) =>
    goi<{ ok: boolean; campaignId?: string; jobs?: string[] }>(
      `/api/fbi/campaigns/${encodeURIComponent(id)}/rerun`, { method: 'POST' }),

  lichDinhKy: () => goi<{ ok: boolean; schedules: LichDinhKy[] }>('/api/fbi/recurring'),

  /**
   * Tạo lịch mới. Bốn trường bắt buộc, backend chặn nếu thiếu
   * (recurring.routes.js:46-57): message, targets, times, fbUid.
   */
  taoLichDinhKy: (than: {
    name?: string;
    message: string;
    targets: string[];
    times: string[];
    fbUid: string;
    media?: unknown[];
    isAnonymous?: boolean;
    safeMode?: boolean;
    delayFromSec?: number;
    delayToSec?: number;
    startDate?: string | null;
    endDate?: string | null;
  }) =>
    goi<{ ok: boolean; schedule: LichDinhKy }>('/api/fbi/recurring', {
      method: 'POST',
      body: JSON.stringify(than),
    }),

  /**
   * Bật/tắt lịch. Backend CHỈ nhận ACTIVE hoặc PAUSED
   * (recurring.routes.js:86) — COMPLETED do bộ chạy nền tự đặt khi quá hạn.
   */
  doiTrangThaiLich: (id: string, status: 'ACTIVE' | 'PAUSED') =>
    goi<{ ok: boolean; schedule: LichDinhKy }>(
      `/api/fbi/recurring/${encodeURIComponent(id)}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),

  xoaLichDinhKy: (id: string) =>
    goi<{ ok: boolean }>(`/api/fbi/recurring/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  trangThaiAI: () => goi<TrangThaiAI>('/api/fbi/ai/status'),
  /**
   * Viết lại các đoạn được bọc `{{...}}` thành cú pháp spin `{a|b|c}`.
   *
   * `variants` là SỐ BIẾN THỂ MONG MUỐN, không phải số bài. Máy chủ ép về khoảng
   * 2..6 (aiRewrite.js:148), nên xin 30 vẫn chỉ nhận tối đa 6 — gọi bên nhận phải
   * đọc `segments` để biết thực tế được bao nhiêu, đừng tin con số mình gửi đi.
   *
   * `text` trả về là MỘT chuỗi đã cắm sẵn cú pháp spin, không phải danh sách bài.
   * Việc chia biến thể cho từng nhóm do bộ spin cục bộ làm lúc đăng
   * (dispatch.routes.js:91).
   */
  aiVietLai: (text: string, variants?: number) =>
    goi<{
      ok: boolean;
      text: string;
      message?: string;
      segments?: { goc: string; bienThe: string[]; canhBao?: string }[];
    }>('/api/fbi/ai/rewrite', {
      method: 'POST',
      body: JSON.stringify({ text, variants }),
    }),

  baiDeBinhLuan: () => goi<{ ok: boolean; posts: BaiDeBinhLuan[]; total: number }>('/api/fbi/comments/postable'),
  guiBinhLuan: (than: {
    targets: string[];
    comments: string[];
    delayFromSec: number;
    delayToSec: number;
    safeMode: boolean;
    fbUid?: string;
  }, idempotencyKey: string) => goi<KetQuaGuiBinhLuan>('/api/fbi/comments/dispatch', {
    method: 'POST',
    body: JSON.stringify(than),
    headers: { 'Idempotency-Key': idempotencyKey },
    hanGioMs: 30000,
  }),
  boCauBinhLuan: () => goi<{ ok: boolean; collections: BoCauBinhLuan[] }>('/api/fbi/comments/collection'),
  luuBoCauBinhLuan: (than: { id?: string; name: string; items: string[] }) =>
    goi<{ ok: boolean; collection: Pick<BoCauBinhLuan, 'id' | 'name' | 'count'> }>('/api/fbi/comments/collection', {
      method: 'POST',
      body: JSON.stringify(than),
    }),

  mcpSessions: () => goi<DanhSachMcpSession>('/api/fbi/mcp-sessions'),
  taoMcpSession: (than: TaoMcpSessionInput) => goi<TaoMcpSessionResponse>('/api/fbi/mcp-sessions', {
    method: 'POST',
    body: JSON.stringify(than),
  }),
  huyMcpSession: (id: string) => goi<{ ok: boolean; revoked: boolean; sessionId: string }>(
    `/api/fbi/mcp-sessions/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),
  mcpAudits: (limit = 100) => goi<DanhSachMcpAudit>(`/api/fbi/mcp-audits?limit=${encodeURIComponent(String(limit))}`),
  mcpWriteProposals: (status = 'PENDING_APPROVAL', limit = 100) =>
    goi<DanhSachMcpWriteProposal>(`/api/fbi/mcp-write-proposals?status=${encodeURIComponent(status)}&limit=${encodeURIComponent(String(limit))}`),
  approveMcpWriteProposal: (id: string, approvalOperationId: string) =>
    goi<McpWriteProposalActionResponse>(`/api/fbi/mcp-write-proposals/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approvalOperationId }),
    }),
  rejectMcpWriteProposal: (id: string, reason?: string) =>
    goi<McpWriteProposalActionResponse>(`/api/fbi/mcp-write-proposals/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || '' }),
    }),

  // ===== Gói dịch vụ =====
  // Ba đường này nằm ở máy chủ gốc (`server.js`), KHÔNG phải `/api/fbi`.
  goi_danhSach: () => goi<{ ok: boolean; plans: Goi[] }>('/api/payments/plans'),

  /**
   * Tạo đơn mua gói. ĐÂY LÀ THAO TÁC GHI, và nó sinh ra một đơn hàng thật.
   *
   * `Idempotency-Key` là BẮT BUỘC, không phải tuỳ chọn: thiếu nó thì bấm hai lần
   * sẽ tạo hai đơn, khách nhìn thấy hai mã chuyển khoản và không biết dùng mã nào.
   * Máy chủ trả lại đúng đơn cũ khi nhận lại cùng khoá (server.js:834).
   */
  goi_taoDon: (planId: string, idempotencyKey: string) =>
    goi<{ ok: boolean; order: DonHang; idempotentReplay?: boolean }>('/api/payments/orders', {
      method: 'POST',
      body: JSON.stringify({ planId }),
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  goi_donCuaToi: () => goi<{ ok: boolean; orders: DonHang[] }>('/api/payments/orders'),

  /**
   * Central server action catalog: read-only declarative scenarios registered by Farmer / Lab Server.
   */
  actionCatalog: () => goi<ActionCatalogResponse>('/api/fbi/action-catalog'),

  /**
   * Customer projection of server-authoritative background presets registry.
   */
  backgroundPresets: () => goi<BackgroundPresetsResponse>('/api/fbi/background-presets'),

  /**
   * Authoritative product capabilities matrix (Final MVP alignment).
   * GET /api/fbi/product-capabilities (ARCH-01 / UI-04 / SEC-01)
   */
  productCapabilities: () => goi<ProductCapabilitiesResponse>('/api/fbi/product-capabilities'),

  // ===== Khuôn payload GraphQL =====
  //
  // KHÔNG có hàm gọi ở đây, và đó là CHỦ Ý.
  //
  // Quản trị khuôn nằm ở trang riêng `/admin` (`public/admin.html`), không phải
  // trong dashboard khách hàng. Dashboard là không gian làm việc của khách;
  // thứ sửa được payload của MỌI khách hàng thì thuộc về khu vận hành.
  //
  // Kiểu dữ liệu `KhuonPayload` / `MaTranKhuon` vẫn giữ trong `kieu.ts` để mô
  // tả hợp đồng API — nếu sau này dashboard cần đọc (chỉ xem, không sửa) thì
  // đã có sẵn.
};

export { goi };
export * from './upgradeHandler';
